import React, { useMemo } from 'react'
import { useNavigate } from 'react-router'

import { SearchBar } from '@/components/search-bar'
import { Skeleton } from '@/components/ui/skeleton'
import { paths } from '@/config/path'
import { useOrders } from '@/features/orders/api/get-orders'
import useDebounce from '@/hooks/use-debounce'
import { useDisclosure } from '@/hooks/use-disclosure'

const ORDER_SN_REGEX = /^(#ORD-(\d{6})|(\d+))$/

export const TechnicianFinder = () => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [query, setQuery] = React.useState<string | undefined>()
  const debouncedQuery = useDebounce(query, 800)
  const isValidQuery = useMemo(
    () => ORDER_SN_REGEX.test(debouncedQuery || '') && debouncedQuery !== undefined && debouncedQuery.length > 0,
    [debouncedQuery],
  )
  const ordersQuery = useOrders({
    filters: [{ field: 'order_number', value: debouncedQuery }],
    page: 1,
    itemsPerPage: 5,
    queryConfig: {
      enabled: isValidQuery,
    },
  })
  const { isOpen, close, open } = useDisclosure()
  const navigate = useNavigate()

  const handleOnChange = (value: string) => {
    // check if value matches the format #ORD-123456 or is just a number
    const orderNumberMatch = value.match(ORDER_SN_REGEX)
    if (orderNumberMatch) {
      const orderNumber = orderNumberMatch[2] || orderNumberMatch[3]
      setQuery(orderNumber)
    } else {
      setQuery(value)
    }
  }

  const handleOrderClick = (orderId: string) => {
    navigate(paths.app.order.getHref(orderId))
    close()
  }

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        close()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [close])

  return (
    <div ref={containerRef} className="flex flex-col items-center gap-2 w-full relative">
      <SearchBar value={query} onValueChange={handleOnChange} placeholder="#ORD-000001" onFocus={open} />
      {isOpen &&
        isValidQuery &&
        (ordersQuery.isPending ? (
          <Container type="div">
            <Skeleton className="w-full h-8 bg-secondary" />
          </Container>
        ) : ordersQuery.isError ? (
          <Container type="div">
            <span className="p-4">Erreur lors de la recherche</span>
          </Container>
        ) : ordersQuery.data.data ? (
          <Container type="list">
            {ordersQuery.data.data.map((order) => {
              const orderNumberStr = '#ORD-' + order.order_number.toString().padStart(6, '0')
              return (
                <li
                  key={order.id}
                  className="flex flex-row items-center justify-between p-2 hover:bg-muted-foreground/5 hover:rounded-md cursor-pointer"
                  onClick={() => handleOrderClick(order.id)}
                >
                  <span className="truncate text-xs font-semibold">
                    {order.mode === 'collab' ? order.practician?.display_name : order.label}
                  </span>
                  <span className="text-xs text-muted-foreground">{orderNumberStr}</span>
                </li>
              )
            })}
          </Container>
        ) : (
          <Container type="div">
            <span className="p-4">Aucun résultat trouvé</span>
          </Container>
        ))}
    </div>
  )
}

function Container({ type, children }: { type: 'list' | 'div'; children: React.ReactNode }) {
  const containerClassName =
    'flex flex-col overflow-y-auto max-h-96 w-full bg-muted rounded-md absolute top-full left-0 z-10 border border-border'
  if (type === 'div') return <div className={containerClassName}>{children}</div>
  return (
    <ul id="search-results" className={containerClassName}>
      {children}
    </ul>
  )
}
